import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [status, setStatus] = useState('loading');
  const [device, setDevice] = useState(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('device_token');
    if (token) {
      try {
        const res = await api.get('/auth/verify');
        setDevice(res.data.device);
        setStatus('authenticated');
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('device_token');
          setStatus('needToken');
        } else {
          setStatus('authenticated');
        }
      }
    } else {
      try {
        const res = await api.get('/devices/check-setup');
        setStatus(res.data.initialized ? 'needToken' : 'setup');
      } catch {
        setStatus('setup');
      }
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  // Listen for auth expiration from API interceptor
  useEffect(() => {
    const handler = () => { setDevice(null); setStatus('needToken'); };
    window.addEventListener('auth-expired', handler);
    return () => window.removeEventListener('auth-expired', handler);
  }, []);

  const login = useCallback((token) => {
    localStorage.setItem('device_token', token);
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(() => {
    localStorage.removeItem('device_token');
    setDevice(null);
    setStatus('needToken');
  }, []);

  return (
    <AuthContext.Provider value={{ status, device, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
}
